import React from "react";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const services = [
  {
    logo_images: "../assets/icons/ansible/service-1.png",
    title: "Configuration Management",
    description:
      "Codify server configuration with reusable Ansible roles, inventories, and variables to ensure every environment remains consistent and compliant.",
  },
  {
    logo_images: "../assets/icons/ansible/service-2.png",
    title: "Playbook Automation",
    description:
      "Design modular playbooks for provisioning, patching, middleware installs, and application deployments with idempotent execution and detailed reporting.",
  },
  {
    logo_images: "../assets/icons/ansible/service-3.png",
    title: "Provisioning & Orchestration",
    description:
      "Automate cloud and on-prem provisioning workflows across AWS, Azure, GCP, VMware, and bare-metal using Ansible collections and dynamic inventories.",
  },
  {
    logo_images: "../assets/icons/ansible/service-4.png",
    title: "CI/CD Integration",
    description:
      "Embed Ansible in Jenkins, GitHub Actions, or GitLab pipelines to drive infrastructure changes, compliance checks, and deployment orchestration from code commits.",
  },
  {
    logo_images: "../assets/icons/ansible/service-5.png",
    title: "Ansible Tower / AWX",
    description:
      "Implement RBAC, workflow templates, surveys, and API-driven executions with Ansible Tower or AWX for centralized governance and auditing.",
  },
];

const benefits = [
  "Eliminate configuration drift across multi-environment estates",
  "Accelerate provisioning cycles with reusable automation assets",
  "Reduce manual errors through idempotent, tested playbooks",
  "Gain full auditability with version-controlled automation",
  "Enable self-service workflows for development and operations teams",
  "Scale automation securely with Tower/AWX policies and RBAC",
];

const expertise = [
  "Red Hat Certified Ansible Automation Engineers",
  "Experience across hybrid estates (Linux, Windows, network, security devices)",
  "Role and collection design patterns for enterprise reuse",
  "Secrets management via Vault, AWS KMS, Azure Key Vault, and HashiCorp Vault",
  "GitOps workflows linking Ansible with Terraform and Kubernetes",
  "Compliance automation for CIS, PCI, HIPAA, and internal controls",
];

const useCases = [
  {
    title: "Global Server Hardening",
    description:
      "Delivered Ansible roles enforcing CIS benchmarks across 3,500 Linux and Windows servers, reducing manual effort by 85% and ensuring continuous compliance.",
  },
  {
    title: "Cloud Provisioning Factory",
    description:
      "Built Ansible playbooks and Tower workflows to provision AWS landing zones, EKS clusters, and supporting services triggered via ServiceNow requests.",
  },
  {
    title: "Application Release Automation",
    description:
      "Integrated Ansible with Jenkins multi-branch pipelines to orchestrate blue/green deployments for 25+ microservices spanning on-prem and cloud.",
  },
  {
    title: "Network Device Automation",
    description:
      "Automated configuration management for firewalls and switches using Ansible network collections, delivering zero-touch updates and drift detection.",
  },
];

const reasons = [
  "Proven playbook accelerators and reference architectures",
  "Security-first approach with credential rotation and encrypted vaults",
  "Tight integration with CI/CD, ITSM, and monitoring ecosystems",
  "Flexible engagement models: advisory, implementation, and managed services",
  "24/7 support for mission-critical automation workflows",
  "Measurable ROI through automation maturity assessments and KPIs",
];

const scrollToContact = () => {
  document
    .getElementById("contact-section")
    ?.scrollIntoView({ behavior: "smooth" });
};

const AnsibleAutomation = () => {
  return (
    <>
      <Helmet>
        <meta
          name="description"
          content="Ayanshtech Solutions — Ansible automation services covering configuration management, playbooks, provisioning, and CI/CD integration for enterprise DevOps."
        />
        <meta name="copyright" content="codeglim" />
        <meta
          property="og:title"
          content="Ansible Automation | Configuration Management Services | Ayanshtech Solutions"
        />
        <meta property="og:site_name" content="Ayanshtech Solutions" />
        <meta
          property="og:url"
          content="http://ayanshtechnology.com/devops-ansible"
        />
        <meta
          property="og:description"
          content="Ansible automation for configuration management, provisioning, and CI/CD pipelines."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />
        <link rel="canonical" href="http://ayanshtechnology.com/devops-ansible" />
        <title>
          Ansible Automation | Configuration Management Services | Ayanshtech
          Solutions
        </title>
      </Helmet>

      <div className="flex flex-col items-center justify-center mb-6 p-6">
        <div className="py-5 w-full">
          <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
            <div className="lg:w-3/4 lg:text-left p-4">
              <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                Ansible Automation
              </h1>
              <p className="sm:text-base md:text-lg lg:text-lg mb-3">
                Standardize configuration management and provisioning workflows
                with Ansible playbooks, roles, and Tower governance. We build
                enterprise-grade automation that keeps environments secure,
                compliant, and deployment-ready.
              </p>
              <button
                onClick={scrollToContact}
                className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
              >
                Talk to Ansible Experts
              </button>
            </div>
            <div className="flex justify-center mb-12 lg:mb-0">
              <img
                src="../assets/images/devops-pages/Ansible-banner.png"
                alt="Ansible Automation"
                className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* <div className="mb-5 mt-10 p-4">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            About Ansible & Configuration Management
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Ansible delivers agentless automation across infrastructure, network,
            and application layers. With declarative playbooks, teams ensure every
            system matches the desired state while minimizing manual effort.
          </p>
        </div> */}

        <section className="text-center mb-5 mt-12">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Ansible Services
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Automation offerings spanning playbook design, provisioning, and
            governance.
          </p>
        </section>
        <div className="rounded-3xl p-6 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service.title}
                className="relative flex flex-col space-y-4     rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"

              >
                <div className="flex items-center gap-2">
                    <div className="w-[60px] h-[60px]  ">
                      <img src={service.logo_images} className="w-full" alt="" />
                    </div>
                  <h3 className="text-lg font-bold">{service.title}</h3>
                  </div>
                <p className="text-lg text-gray-700">{service.description}</p>
              </div>
            ))}
          </div>
        </div>

        <section className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Benefits of Ansible Automation
          </h2>
        </section>
        <div className="p-4 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-start space-x-4   rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none "
              >
                <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>

        <section className="p-6 sm:px-8 md:px-12 lg:px-16 w-full rounded-3xl mt-10 ">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Our Ansible Expertise
            </h2>
          </div>
          <div className="flex justify-center item-center flex-col md:flex-row ">
          <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-6xl my-auto space-y-3 text-gray-700">
            {expertise.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className="w-full md:w-1/2 lg:w-[40%]">
           <img src="../assets/images/devops-pages/ansible-image.png" alt="" />
          </div>
          </div>
        </section>

        <section className="text-center mb-5 mt-10">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Use Cases
          </h2>
        </section>
        <div className="rounded-3xl p-6 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {useCases.map((useCase) => (
              <div
                key={useCase.title}
                className="relative flex flex-col space-y-4   rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none "

              >
                <h3 className="text-lg font-bold text-gray-900">
                  {useCase.title}
                </h3>
                <p className="text-lg text-gray-700">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>

        <section className="text-center mb-5 mt-10">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Why Choose Ayanshtech
          </h2>
        </section>
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl ">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reasons.map((reason) => (
              <div
                key={reason}
                className="flex items-start space-x-4   rounded-2xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none  "
              >
                <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-700">{reason}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <button
              onClick={scrollToContact}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
            >
              Schedule an Automation Workshop
            </button>
          </div>
        </div>

        <div id="contact-section" className="w-full mt-12">
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default AnsibleAutomation;

